import { useState, useEffect, useCallback } from "react";
import { Badge, DataTable, Link, Page, Card, Button, Tabs, Pagination, BlockStack, Text, EmptyState } from "@shopify/polaris";
import { alphabetize, getRequest, patchRequest } from "../../shared/components/functions";
import { NewWorkorderModal } from "./components/newWorkorderModal";
import { useLookupStore } from "../../utils/useStore";
import Select from "react-select";
import { Toasts } from "../../shared/components/toasts/toasts";
import { DeleteIcon } from "@shopify/polaris-icons";
import { PageSpinner } from "../../shared/components/spinner/PageSpinner";
import { ImageThumbNailViewer, getPrimaryS3ImageKey } from "../../shared/components/s3_image_display/ImageThumbNailViewer";

const COMPLETED_PAGE_SIZE = 50;

/*
  Fulfilement list: 
  - show all the goods that need to be fulfilled and show if we can produce them or not
  
  Sales order page:
    Erdem wants:
      -> reserve: 
          - reserve certain stock for an orderline

      -> fulfill:
          - fulfill stock for an orderline
            * mark orderline as (partially) fulfilled
            * decrease product stock
            * write stock change
      -> request:
          - request production for an orderline


    Main questions that need to be answered.
    - What do we need to produce/buy to fulfill all open orders?
    - What orders need to be (partially fulfilled) and by when?
    - What orders are ready to be shipped?
*/

export default function WorkOrdersPage() {
  const users = useLookupStore((state) => state.users);
  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [completedWorkOrders, setCompletedWorkOrders] = useState([]);
  const [newOrderModalOpen, setNewOrderModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [completedLoaded, setCompletedLoaded] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedHasNext, setCompletedHasNext] = useState(false);

  const filteredUsers = users
    .filter((item) => !item.Inactief)
    .sort((a, b) => alphabetize(a, b, "Afkorting"))
    .map((item) => ({ value: item.GebruikerID, label: item.Afkorting }));

  const fetchOpenOrders = useCallback(async () => {
    const items = await getRequest(`/api/workorders`);
    setOpenWorkOrders(items);
    setLoaded(true);
  }, []);

  const fetchCompletedOrders = useCallback(async (page) => {
    setCompletedLoaded(false);
    const items = await getRequest(`/api/workorders?status=FINISHED&page=${page}&limit=${COMPLETED_PAGE_SIZE + 1}`);
    const completedItems = Array.isArray(items) ? items : [];
    setCompletedWorkOrders(completedItems.slice(0, COMPLETED_PAGE_SIZE));
    setCompletedHasNext(completedItems.length > COMPLETED_PAGE_SIZE);
    setCompletedLoaded(true);
  }, []);

  useEffect(() => {
    fetchOpenOrders();
  }, [fetchOpenOrders, newOrderModalOpen]);

  useEffect(() => {
    if (selectedTab === 1) fetchCompletedOrders(completedPage);
  }, [completedPage, fetchCompletedOrders, selectedTab]);

  const handleAssigneeChange = async (value, orderID) => {
    setIsSaving(true);
    await patchRequest(`/api/workorders/${orderID}/assignee`, { user: value?.value || null }, setToast);
    await fetchOpenOrders();
    setIsSaving(false);
  };

  async function cancelWorkOrder(id) {
    if (confirm("Are you sure you want to cancel this work order? This will not consume any inventory.")) {
      await patchRequest(`/api/workorders/${id}/cancel`, {}, setToast);
      await fetchOpenOrders();
    }
  }

  const getOrderCells = (order, assigneeCell) => [
    <Link removeUnderline url={`/workorders/${order.Id}`}>
      #{order.Id}
    </Link>,
    <Link removeUnderline url={`/products/${order.OnderdelenId}`}>
      #{order.OnderdelenId}
    </Link>,
    <ImageThumbNailViewer s3Key={getPrimaryS3ImageKey(order.Image)} size={48} key={`workorder_img_${order.Id}`} />,
    order.ProductNameNL,
    order.Qty,
    order.VariantName,
    order.StructureName,
    <Badge>{order.VariantName && order.StructureName ? "TREE" : "BOM"}</Badge>,
    order.CreatedAt?.substring(0, 10),
    order.AssignmentDate?.substring(0, 10),
    order.DueDate?.substring(0, 10),
    order.OrderbevestigingID ? (
      <Link removeUnderline url={`/sales/${order.OrderbevestigingID}`}>
        {order.OrderbevestigingID}
      </Link>
    ) : (
      ""
    ),
    assigneeCell,
    <Badge>{order.Status}</Badge>,
  ];

  const rowsActive = Array.isArray(openWorkOrders)
    ? openWorkOrders.map((order) => [
        ...getOrderCells(
          order,
          <Select
            id={`CreatedByUserId-${order.Id}`}
            options={filteredUsers}
            onChange={(e) => handleAssigneeChange(e, order.Id)}
            value={filteredUsers.find((c) => parseInt(c.value) === parseInt(order.CreatedByUserId)) || ""}
            isDisabled={isSaving}
            menuPortalTarget={document.body}
            isClearable
            styles={{
              input: (base) => ({ ...base, marginTop: "-10px" }),
              indicatorSeperator: (base) => ({ ...base, height: "26px" }),
              indicatorsContainer: (base) => ({ ...base, height: "26px" }),
              control: (base) => ({ ...base, height: "26px", minHeight: "26px", borderRadius: "8px", borderColor: "#919191" }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        ),
        <Button icon={DeleteIcon} onClick={() => cancelWorkOrder(order.Id)} size="slim" tone="critical" />,
      ])
    : [];

  const rowsCompleted = Array.isArray(completedWorkOrders)
    ? completedWorkOrders.map((order) => getOrderCells(order, order.Afkorting || ""))
    : [];

  const tableHeadings = [
    <b>ID</b>,
    <b>Product ID</b>,
    <b>Image</b>,
    <b>Product name</b>,
    <b>Qty</b>,
    <b>Variant</b>,
    <b>Structure</b>,
    <b>Type</b>,
    <b>Created</b>,
    <b>Assignment</b>,
    <b>Due</b>,
    <b>Sale</b>,
    <b>Assignee</b>,
    <b>Status</b>,
  ];

  const tabs = [
    {
      id: "open",
      content: "Open",
    },
    {
      id: "completed",
      content: "Completed",
    },
  ];

  const completedFooter = (
    <BlockStack inlineAlign="center">
      <div style={{ marginBottom: "10px" }}>
        <Text as="span" tone="subdued">
          Page {completedPage}
        </Text>
      </div>
      <Pagination
        hasPrevious={completedPage > 1}
        onPrevious={() => {
          if (completedPage > 1) setCompletedPage(completedPage - 1);
        }}
        hasNext={completedHasNext}
        onNext={() => {
          if (completedHasNext) setCompletedPage(completedPage + 1);
        }}
      />
    </BlockStack>
  );

  const openWorkOrdersTable =
    rowsActive.length > 0 ? (
    <DataTable
      verticalAlign="middle"
      columnContentTypes={["text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "numeric"]}
      headings={[...tableHeadings, <b>Cancel</b>]}
      rows={rowsActive}
    />
    ) : (
      <div style={{ padding: "32px 0" }}>
        <EmptyState
          heading="No open work orders"
          action={{ content: "New work order", onAction: () => setNewOrderModalOpen(true) }}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png">
          <Text as="p" tone="subdued">
            All production work is clear. New work orders will appear here as soon as they are created.
          </Text>
        </EmptyState>
      </div>
    );

  const completedWorkOrdersTable = completedLoaded ? (
    <DataTable
      verticalAlign="middle"
      columnContentTypes={["text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text", "text"]}
      headings={tableHeadings}
      rows={rowsCompleted}
      footerContent={completedFooter}
    />
  ) : (
    <BlockStack inlineAlign="center">
      <div style={{ padding: "24px" }}>
        <Text as="p" tone="subdued">
          Loading completed work orders...
        </Text>
      </div>
    </BlockStack>
  );

  if (!loaded) return <PageSpinner />;

  return (
    <Page title="Work orders" fullWidth primaryAction={{ content: "New work order", onAction: () => setNewOrderModalOpen(true) }}>
      <Toasts toast={toast} setToast={setToast} />
      <Card>
        <NewWorkorderModal modalActive={newOrderModalOpen} setModalActive={setNewOrderModalOpen} setToast={setToast} />
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          {selectedTab === 0 ? openWorkOrdersTable : completedWorkOrdersTable}
        </Tabs>
      </Card>
    </Page>
  );
}
